"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type TrekkingDisplayPreviewProps = {
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

/*
 * Alpine sunrise: a snow-capped peak catches first light on its east face,
 * the sun breaks over the saddle beside it, and mist lies between the
 * forested ridges. A trail switchbacks up out of the foreground; a waymark
 * signpost holds the left third, a boulder with a pack and poles the right.
 *
 * One panoramic SVG (640×220, `slice`): a 301px feed card shows the composed
 * centre, a full-width post reveals more range. The trail's foot sits under
 * the bottom-centre participant pill. Motion is one compositor-only CSS loop
 * (the mist drifting), off under reduced motion.
 */
const TREKKING_KEYFRAMES = `
@keyframes trek-mist { 0%, 100% { transform: translateX(-6px) } 50% { transform: translateX(6px) } }
.trek-mist { animation: trek-mist 14s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) { .trek-mist { animation: none } }
`;

/** A row of pine tops along a ridge crest. */
const pines = (
  from: number,
  to: number,
  step: number,
  crest: (x: number) => number,
  height: number,
) => {
  const points: string[] = [];
  for (let x = from; x <= to; x += step) {
    const jitter = ((x * 7919) % 13) / 13;
    const h = height * (0.7 + jitter * 0.6);
    const y = crest(x);
    points.push(`${x - step / 2},${y} ${x},${y - h} ${x + step / 2},${y}`);
  }
  return points;
};

const NEAR_RIDGE = (x: number) =>
  150 + 10 * Math.sin(x / 70) + 6 * Math.sin(x / 23);
const FAR_RIDGE = (x: number) =>
  128 + 8 * Math.sin(x / 55 + 1) + 4 * Math.sin(x / 17);
const NEAR_PINES = pines(-10, 650, 9, NEAR_RIDGE, 16);
const FAR_PINES = pines(-10, 650, 7, FAR_RIDGE, 10);
const ridgePath = (crest: (x: number) => number, bottom: number) => {
  let d = `M-10 ${bottom} L-10 ${crest(-10).toFixed(1)}`;
  for (let x = 0; x <= 650; x += 10) d += ` L${x} ${crest(x).toFixed(1)}`;
  return `${d} L650 ${bottom} Z`;
};
const NEAR_RIDGE_PATH = ridgePath(NEAR_RIDGE, 220);
const FAR_RIDGE_PATH = ridgePath(FAR_RIDGE, 220);

export function TrekkingDisplayPreview({
  participants,
}: TrekkingDisplayPreviewProps) {
  const uid = useId().replace(/[^\w-]/g, "");
  const id = (name: string) => `trek-${uid}-${name}`;
  const url = (name: string) => `url(#${id(name)})`;

  return (
    <ActivityScenePreview
      label="Trekking"
      backgroundClassName="bg-[#2a3366]"
      participants={participants}
      scene={
        <>
          <style href="trekking-scene-keyframes" precedence="default">
            {TREKKING_KEYFRAMES}
          </style>
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 640 220"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
            focusable="false"
          >
            <defs>
              <linearGradient id={id("sky")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#1f2c62" />
                <stop offset="0.3" stopColor="#56508f" />
                <stop offset="0.52" stopColor="#e2897c" />
                <stop offset="0.66" stopColor="#ffcf94" />
              </linearGradient>
              <radialGradient id={id("sun")}>
                <stop offset="0" stopColor="#fffbea" />
                <stop offset="0.14" stopColor="#ffe6a8" stopOpacity="0.9" />
                <stop offset="0.45" stopColor="#ffb07a" stopOpacity="0.3" />
                <stop offset="1" stopColor="#ff9a6a" stopOpacity="0" />
              </radialGradient>
              <linearGradient id={id("lit")} x1="0" x2="1" y1="0" y2="1">
                <stop offset="0" stopColor="#f4a48a" />
                <stop offset="1" stopColor="#9c4e6a" />
              </linearGradient>
              <linearGradient id={id("shade")} x1="1" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#5a5a9c" />
                <stop offset="1" stopColor="#2c2f66" />
              </linearGradient>
              <linearGradient id={id("far")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#8c6c9e" />
                <stop offset="1" stopColor="#b9809a" />
              </linearGradient>
              <linearGradient id={id("ridgeFar")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#3a4a6a" />
                <stop offset="1" stopColor="#24304a" />
              </linearGradient>
              <linearGradient id={id("ridgeNear")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#1d3a36" />
                <stop offset="1" stopColor="#0f2220" />
              </linearGradient>
              <linearGradient id={id("meadow")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#6f7a3a" />
                <stop offset="0.4" stopColor="#3e5a2a" />
                <stop offset="1" stopColor="#1c2e18" />
              </linearGradient>
              <linearGradient id={id("rock")} x1="0" x2="1" y1="0" y2="1">
                <stop offset="0" stopColor="#c9b8b0" />
                <stop offset="0.55" stopColor="#7c7480" />
                <stop offset="1" stopColor="#3e3a48" />
              </linearGradient>
              <linearGradient id={id("pack")} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="#ff8a4a" />
                <stop offset="1" stopColor="#c2401e" />
              </linearGradient>
              <linearGradient id={id("plank")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#c99a62" />
                <stop offset="1" stopColor="#8a5a2e" />
              </linearGradient>
            </defs>

            <rect width="640" height="150" fill={url("sky")} />
            {/* Last stars */}
            {[
              [40, 14],
              [120, 26],
              [214, 10],
              [470, 18],
              [540, 30],
              [610, 12],
            ].map(([x, y]) => (
              <circle key={x} cx={x} cy={y} r="0.8" fill="#fff" opacity="0.7" />
            ))}

            {/* Far range, hazed */}
            <path
              d="M-10 120 L30 96 L70 108 L120 82 L170 100 L210 90 L250 110 L300 104 L340 112 L520 104 L560 80 L600 96 L650 88 L650 140 L-10 140 Z"
              fill={url("far")}
            />

            {/* Sun breaking over the saddle */}
            <circle cx="296" cy="88" r="96" fill={url("sun")} />
            <circle cx="296" cy="88" r="10" fill="#fffbee" />

            {/* Left peak */}
            <path d="M120 130 L222 62 L330 130 Z" fill={url("shade")} />
            <path
              d="M222 62 L330 130 L262 130 L240 96 Z"
              fill={url("lit")}
              opacity="0.85"
            />
            <path
              d="M222 62 L208 76 L214 78 L218 74 L224 80 L232 74 L238 76 Z"
              fill="#f2e8f6"
            />

            {/* The main peak, first light on its east face */}
            <path d="M232 132 L398 36 L586 132 Z" fill={url("shade")} />
            <path
              d="M398 36 L586 132 L470 132 L430 90 L414 70 Z"
              fill={url("lit")}
            />
            {/* Snowcap: blue in shade, pink in the sun */}
            <path
              d="M398 36 L374 52 L382 54 L372 64 L384 62 L392 70 L398 60 L404 66 Z"
              fill="#dfe2f6"
            />
            <path
              d="M398 36 L404 66 L414 58 L420 68 L428 60 L438 66 L420 46 Z"
              fill="#ffe6dc"
            />
            {/* Couloirs */}
            <path
              d="M392 72 L380 98 M408 80 L414 106 M362 92 L350 116"
              stroke="#c9c6ea"
              strokeOpacity="0.28"
              strokeWidth="1"
            />
            <path
              d="M398 36 L586 132"
              stroke="#ffd6b0"
              strokeOpacity="0.6"
              strokeWidth="1"
            />
            {/* Summit flag */}
            <line
              x1="398"
              y1="36"
              x2="398"
              y2="27"
              stroke="#2a2030"
              strokeWidth="0.8"
            />
            <path d="M398 27 L405 29 L398 31 Z" fill="#ff5a4a" />

            {/* Mist pooled between the ridges */}
            <g className="trek-mist">
              <ellipse
                cx="200"
                cy="130"
                rx="190"
                ry="9"
                fill="#ffe6dc"
                opacity="0.4"
              />
              <ellipse
                cx="480"
                cy="134"
                rx="200"
                ry="8"
                fill="#ffe6dc"
                opacity="0.32"
              />
            </g>

            {/* Forested ridges */}
            <path d={FAR_RIDGE_PATH} fill={url("ridgeFar")} />
            {FAR_PINES.map((points) => (
              <polygon key={points} points={points} fill="#24304a" />
            ))}
            <path d={NEAR_RIDGE_PATH} fill={url("ridgeNear")} />
            {NEAR_PINES.map((points) => (
              <polygon key={points} points={points} fill="#13282a" />
            ))}

            {/* Meadow in the foreground, sunlit crest */}
            <path
              d="M-10 176 Q150 160 320 168 T650 172 L650 220 L-10 220 Z"
              fill={url("meadow")}
            />
            <path
              d="M-10 176 Q150 160 320 168 T650 172"
              fill="none"
              stroke="#ffd59a"
              strokeOpacity="0.5"
              strokeWidth="1.2"
            />

            {/* The trail, switchbacking up into the trees */}
            <path
              d="M332 226 Q284 204 312 190 Q352 178 326 168 Q304 160 330 152"
              fill="none"
              stroke="#5a4428"
              strokeOpacity="0.5"
              strokeLinecap="round"
              strokeWidth="9"
            />
            <path
              d="M332 226 Q284 204 312 190 Q352 178 326 168 Q304 160 330 152"
              fill="none"
              stroke="#e8c79a"
              strokeOpacity="0.85"
              strokeLinecap="round"
              strokeWidth="6"
            />

            {/* Waymark signpost, left third */}
            <g>
              <ellipse
                cx="204"
                cy="186"
                rx="14"
                ry="2.6"
                fill="#0f1a0e"
                opacity="0.6"
              />
              <rect x="201" y="108" width="5" height="78" fill={url("plank")} />
              <rect
                x="205"
                y="108"
                width="1"
                height="78"
                fill="#ffd9a0"
                opacity="0.6"
              />
              <path
                d="M184 112 L232 112 L240 120 L232 128 L184 128 Z"
                fill={url("plank")}
              />
              <path
                d="M184 112 L232 112 L240 120"
                fill="none"
                stroke="#ffe2b0"
                strokeWidth="0.9"
              />
              <text
                x="208"
                y="123.2"
                textAnchor="middle"
                fontSize="7.4"
                fontWeight="800"
                fill="#3a2414"
                letterSpacing="0.6"
                style={{ fontFamily: "inherit" }}
              >
                SUMMIT
              </text>
              <path
                d="M226 134 L182 134 L174 142 L182 150 L226 150 Z"
                fill={url("plank")}
              />
              <text
                x="202"
                y="145.2"
                textAnchor="middle"
                fontSize="7.4"
                fontWeight="800"
                fill="#3a2414"
                letterSpacing="0.6"
                style={{ fontFamily: "inherit" }}
              >
                4.2 KM
              </text>
              {/* Trail blaze */}
              <rect x="201" y="158" width="5" height="4" fill="#ff5a4a" />
            </g>

            {/* Boulder with a pack and poles, right third */}
            <g transform="translate(-12 0)">
              <ellipse
                cx="440"
                cy="186"
                rx="34"
                ry="3.6"
                fill="#0f1a0e"
                opacity="0.6"
              />
              <path
                d="M408 186 Q404 160 424 152 Q446 144 462 156 Q476 168 472 186 Z"
                fill={url("rock")}
              />
              <path
                d="M424 152 Q446 144 462 156"
                fill="none"
                stroke="#ffd6b8"
                strokeWidth="1.2"
                opacity="0.8"
              />
              {/* Poles */}
              <line
                x1="404"
                y1="186"
                x2="430"
                y2="122"
                stroke="#d8dce6"
                strokeWidth="1.6"
              />
              <line
                x1="414"
                y1="186"
                x2="438"
                y2="124"
                stroke="#9aa0b0"
                strokeWidth="1.6"
              />
              <rect
                x="427"
                y="118"
                width="4"
                height="8"
                rx="1.5"
                fill="#1c1c22"
                transform="rotate(22 429 122)"
              />
              <rect
                x="435"
                y="120"
                width="4"
                height="8"
                rx="1.5"
                fill="#1c1c22"
                transform="rotate(22 437 124)"
              />
              {/* Pack */}
              <path
                d="M440 176 L440 150 Q440 138 452 138 Q464 138 464 150 L464 176 Z"
                fill={url("pack")}
              />
              <path
                d="M440 150 Q440 138 452 138 Q464 138 464 150 Z"
                fill="#a8301a"
              />
              <rect
                x="444"
                y="158"
                width="16"
                height="12"
                rx="3"
                fill="#d4562e"
              />
              <rect
                x="444"
                y="158"
                width="16"
                height="2"
                rx="1"
                fill="#ffb07a"
              />
              <rect
                x="438"
                y="176"
                width="28"
                height="7"
                rx="3.5"
                fill="#3e5a7a"
              />
              <path
                d="M441 140 Q446 136 452 137"
                fill="none"
                stroke="#ffd0a8"
                strokeWidth="1"
              />
            </g>

            {/* Lone pines framing the outer edges (seen on wider cards) */}
            {[
              { x: 40, h: 70 },
              { x: 74, h: 50 },
              { x: 580, h: 56 },
              { x: 612, h: 76 },
            ].map((tree) => (
              <g key={tree.x}>
                <polygon
                  points={`${tree.x},${200 - tree.h} ${tree.x - tree.h * 0.26},200 ${tree.x + tree.h * 0.26},200`}
                  fill="#0e1f1c"
                />
                <rect
                  x={tree.x - 1.5}
                  y="198"
                  width="3"
                  height="10"
                  fill="#1b130d"
                />
              </g>
            ))}

            {/* A pair of birds over the valley */}
            {[
              { x: 276, y: 70 },
              { x: 288, y: 62 },
            ].map((bird) => (
              <path
                key={bird.x}
                d={`M${bird.x - 4} ${bird.y - 1.6} Q${bird.x - 1.6} ${bird.y - 2.4} ${bird.x} ${bird.y} Q${bird.x + 1.6} ${bird.y - 2.4} ${bird.x + 4} ${bird.y - 1.6}`}
                fill="none"
                stroke="#2a2040"
                strokeWidth="1"
                strokeLinecap="round"
              />
            ))}
          </svg>
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_115%_125%_at_50%_45%,transparent_52%,rgba(12,16,40,0.5)_100%)]" />
      }
    />
  );
}
